import _ from "lodash";
import React from "react";

import { BehavioralGoalsRating } from "@/components/BehavioralGoalsRating";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { makeCnaiProps } from "@/lib/helpers/clinically-ai";
import { RATING_OPTION_LABEL_MAP } from "@/constants/rating";
import { SectionCardWrapper } from "@/components/SectionCardWrapper";
import { slugify } from "@/lib/helpers/slugify";
import { TextArea } from "@/components/UI/StyledInput";

// isCnaiEnabled: this component derives its own cnai field ids from diagnosis data, so callers
// can't pass a field descriptor in — they opt this instance in instead. PrescribeNewNote.jsx
// renders this component once for desktop and once for mobile (CSS-toggled, both mounted at
// once), so only one of those instances may be tagged — otherwise the same
// cnai-template-field-id appears twice in one template container.
export const PrescribeDiagnosisProgress = (props) => {
  const { editable, editHandler, isCnaiEnabled, note, profileView } = props;

  let diagnosisProgress = note.content.diagnosis_progress;

  const [editCopy, setEditCopy] = React.useState(_.cloneDeep(diagnosisProgress));

  const handleEditCopyChange = (e, index, key, isNumber = false) => {
    let editCopyClone = _.cloneDeep(editCopy);

    editCopyClone[index][key] = isNumber ? parseInt(e.target.value) : e.target.value;
    setEditCopy(editCopyClone);

    if (editHandler) {
      editHandler("diagnosis_progress", editCopyClone);
    }
  };

  let content = (
    <>
      <div className="row mx-1 px-1">
        <div className="col-lg-5">
          <p className="clr-sec tw:font-bold">Diagnosis</p>
        </div>
        <div className="col-lg-3 tw:font-bold">
          <p className="clr-sec tw:font-bold">Assessment</p>
        </div>

        <div className="col-lg-3 tw:font-bold">
          <div className="d-flex justify-content-between">
            <p className="clr-sec"> Plan</p>
          </div>
        </div>
      </div>

      {diagnosisProgress.map((progress, index) => {
        let style = { minHeight: 50 };

        if (index !== diagnosisProgress.length - 1) {
          style.borderBottom = "1px  solid #DAE6EC";
        } else if (index === diagnosisProgress.length - 1 && profileView) {
          style.borderBottom = "2px  solid #DAE6EC";
        }

        // slug of the diagnosis label, not just the array index — stays stable if
        // diagnosisProgress ever reorders/filters. Index is still appended as a uniqueness
        // suffix: diagnosisProgress has no dedup, so two diagnoses can share a label (and thus
        // a slug) — without the suffix they'd collide on the same cnai-template-field-id and
        // CNAI would silently merge them (see clinically-ai skill's repo-patterns.md Trap 1).
        // Also covers the label-has-no-alphanumeric-characters case (id must never resolve to
        // an empty string, see clinically-ai.ts) since the suffix alone still slugifies fine.
        const diagnosisSlug = `${slugify(progress.label || "")}-${index}`;

        return (
          <div className="row mt-1 mx-1 px-1" key={index} style={style}>
            <div className="col-lg-5">
              <div>
                {index + 1}. {progress.label}
              </div>

              <div>
                <div className="col-12 d-flex justify-content-start">
                  {Object.values(RATING_OPTION_LABEL_MAP).map((optionLabel, labelIndex) => (
                    <div className="w-25 fs-10 text-center tw:font-bold" key={labelIndex}>
                      <div className="m-auto">{optionLabel}</div>
                    </div>
                  ))}
                </div>

                <BehavioralGoalsRating
                  isEditing={editable}
                  onClick={(e) => handleEditCopyChange(e, index, "answer", true)}
                  optionLabelMap={RATING_OPTION_LABEL_MAP}
                  progress={!editable ? progress : editCopy[index]}
                />
              </div>
            </div>

            <div className="col-lg-3">
              {!editable ? (
                <p>{progress.assessment}</p>
              ) : (
                <TextArea
                  onChange={(e) => handleEditCopyChange(e, index, "assessment")}
                  // id reused from PrescribeWorkflowSteps.js's live-call PrescribeAssessmentPlan
                  // step — this note page and that widget are mutually exclusive (same rationale
                  // as the prescribe-progress-note template id reuse in
                  // DisplayWorkflowStepWithVideoClient.js)
                  textareaProps={
                    isCnaiEnabled
                      ? makeCnaiProps({
                          format: "paragraph",
                          id: `assessment-${diagnosisSlug}`,
                          name: `Assessment - ${progress.label}`,
                          type: "string",
                        })
                      : undefined
                  }
                  value={editCopy[index].assessment}
                />
              )}
            </div>

            <div className="col-lg-3">
              {!editable ? (
                <p>{progress.plan}</p>
              ) : (
                <TextArea
                  onChange={(e) => handleEditCopyChange(e, index, "plan")}
                  // see the Assessment field's id comment above
                  textareaProps={
                    isCnaiEnabled
                      ? makeCnaiProps({
                          format: "paragraph",
                          id: `plan-${diagnosisSlug}`,
                          name: `Plan - ${progress.label}`,
                          type: "string",
                        })
                      : undefined
                  }
                  value={editCopy[index].plan}
                />
              )}
            </div>
          </div>
        );
      })}
    </>
  );

  let desktopContent = (
    <SectionCardWrapper
      Card={DashboardCard2}
      cardProps={{ className: "d-none d-md-none d-lg-block" }}
      profileView={profileView}
    >
      {content}
    </SectionCardWrapper>
  );

  let mobileContent = (
    <div className="d-block d-md-block d-lg-none mt-3">
      <div className="d-flex justify-content-between">
        <p className="cmt-3 clr-sec tw:font-bold">Diagnosis</p>
      </div>

      {diagnosisProgress.map((progress, index) => {
        return (
          <CardSecondary className="mx-auto mt-2 px-3 py-3 d-block" key={index}>
            <div className="col-lg-3 tw:font-bold">
              <p className="clr-sec tw:font-bold">Diagnosis</p>
            </div>

            <div>
              {index + 1}. {progress.label}
            </div>

            <BehavioralGoalsRating
              isEditing={editable}
              onClick={(e) => handleEditCopyChange(e, index, "answer", true)}
              optionLabelMap={RATING_OPTION_LABEL_MAP}
              progress={progress}
            />

            <div className="col-lg-3 tw:font-bold">
              <p className="clr-sec tw:font-bold">Assessment</p>
            </div>
            <div className="col-lg-3">
              {!editable ? (
                <p>{progress.actions}</p>
              ) : (
                <TextArea
                  onChange={(e) => handleEditCopyChange(e, index, "assessment")}
                  value={editCopy[index].actions}
                />
              )}
            </div>

            <div className="col-lg-4 tw:font-bold">
              <p className="clr-sec tw:font-bold">Plan</p>
            </div>
            <div className="col-lg-3">
              {!editable ? (
                <p>{progress.plan}</p>
              ) : (
                <TextArea
                  onChange={(e) => handleEditCopyChange(e, index, "plan")}
                  value={editCopy[index].plan}
                />
              )}
            </div>
          </CardSecondary>
        );
      })}
    </div>
  );

  return (
    <>
      {desktopContent}
      {mobileContent}
    </>
  );
};
